import { X } from 'lucide-react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { emptyCondition } from '../api/trigger-mappers';
import {
  CONDITION_GROUPS,
  DEVICES,
  isConditionField,
  OPS,
  SERVER_FIELDS,
  VALUE_KIND,
  WEEKDAYS,
  type ConditionValueKind,
  type TriggerForm,
} from '../schemas/trigger-schema';

interface TriggerConditionRowProps {
  form: UseFormReturn<TriggerForm>;
  index: number;
  /** A rescuer runs on the server: only the visitor and store facts can be picked. */
  rescuer: boolean;
  currency: string;
  onRemove: () => void;
}

const UNIT: Partial<Record<string, string>> = {
  timeOnPageSeconds: 'seconds',
  timeOnSiteSeconds: 'seconds',
  pageViews: 'pages',
  visits: 'visits',
  previousChats: 'chats',
  hourOfDay: 'hour',
};

/** Field, operator and a value input that fits the field; one row of "all of these". */
export function TriggerConditionRow({
  form,
  index,
  rescuer,
  currency,
  onRemove,
}: TriggerConditionRowProps) {
  const { t } = useTranslation('chatSettings');
  const field = useWatch({ control: form.control, name: `conditions.${index}.field` });
  const kind: ConditionValueKind = isConditionField(field) ? VALUE_KIND[field] : 'text';
  const position = index + 1;

  return (
    <li className="grid gap-3 rounded-md border p-3 sm:grid-cols-[minmax(0,13rem)_minmax(0,10rem)_minmax(0,1fr)_auto] sm:items-start">
      <FormField
        control={form.control}
        name={`conditions.${index}.field`}
        render={({ field: input }) => (
          <FormItem>
            <FormLabel className="sr-only">{t('triggers.conditionField', { position })}</FormLabel>
            <Select
              value={input.value}
              onValueChange={(next) => {
                if (!isConditionField(next)) return;
                // a new field needs its own operator and an empty value
                form.setValue(`conditions.${index}`, emptyCondition(next), { shouldDirty: true });
              }}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Object.entries(CONDITION_GROUPS).map(([group, fields]) => (
                  <SelectGroup key={group}>
                    <SelectLabel>{t(`triggers.groups.${group}`)}</SelectLabel>
                    {fields.map((value) => (
                      <SelectItem
                        key={value}
                        value={value}
                        disabled={rescuer && !SERVER_FIELDS.has(value)}
                      >
                        {t(`triggers.fields.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`conditions.${index}.op`}
        render={({ field: input }) => (
          <FormItem>
            <FormLabel className="sr-only">{t('triggers.conditionOp', { position })}</FormLabel>
            <Select
              value={input.value}
              onValueChange={(next) => {
                // Radix reports '' while the item list changes under a new field
                if (next) input.onChange(next);
              }}
              disabled={OPS[kind].length === 1}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {OPS[kind].map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`triggers.ops.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />
      <ConditionValue form={form} index={index} kind={kind} field={field} currency={currency} />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('triggers.removeCondition', { position })}
        onClick={onRemove}
      >
        <X aria-hidden />
      </Button>
    </li>
  );
}

function ConditionValue({
  form,
  index,
  kind,
  field,
  currency,
}: {
  form: UseFormReturn<TriggerForm>;
  index: number;
  kind: ConditionValueKind;
  field: string;
  currency: string;
}) {
  const { t } = useTranslation('chatSettings');
  const label = t('triggers.conditionValue', { position: index + 1 });

  if (kind === 'list' || kind === 'countries') {
    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.values`}
        render={({ field: input }) => (
          <FormItem>
            <FormLabel className="sr-only">{label}</FormLabel>
            <FormControl>
              <TagInput
                value={input.value}
                onChange={(values) => {
                  input.onChange(
                    kind === 'countries' ? values.map((v) => v.toUpperCase()) : values,
                  );
                }}
                onBlur={input.onBlur}
                max={kind === 'countries' ? 250 : 30}
                maxLength={100}
                placeholder={t(
                  kind === 'countries'
                    ? 'triggers.countriesPlaceholder'
                    : 'triggers.listPlaceholder',
                )}
                removeLabel={(value) => t('triggers.removeValue', { value })}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (kind === 'days' || kind === 'devices') {
    const options = kind === 'days' ? WEEKDAYS : DEVICES;
    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.values`}
        render={({ field: input }) => (
          <FormItem>
            <fieldset className="flex flex-wrap gap-3">
              <legend className="sr-only">{label}</legend>
              {options.map((option) => {
                const id = `trigger-${String(index)}-${option}`;
                return (
                  <div key={option} className="flex items-center gap-1.5">
                    <Checkbox
                      id={id}
                      checked={input.value.includes(option)}
                      onCheckedChange={(checked) => {
                        input.onChange(
                          checked === true
                            ? [...input.value, option]
                            : input.value.filter((v) => v !== option),
                        );
                      }}
                    />
                    <Label htmlFor={id} className="font-normal">
                      {t(
                        kind === 'days' ? `triggers.days.${option}` : `triggers.devices.${option}`,
                      )}
                    </Label>
                  </div>
                );
              })}
            </fieldset>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (kind === 'flag') {
    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field: input }) => (
          <FormItem>
            <FormLabel className="sr-only">{label}</FormLabel>
            <Select value={input.value} onValueChange={input.onChange}>
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="true">{t('triggers.yes')}</SelectItem>
                <SelectItem value="false">{t('triggers.no')}</SelectItem>
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />
    );
  }

  const valueInput = (
    <FormField
      control={form.control}
      name={`conditions.${index}.value`}
      render={({ field: input }) => (
        <FormItem className="min-w-0 flex-1">
          <FormLabel className="sr-only">{label}</FormLabel>
          <div className="flex items-center gap-2">
            <FormControl>
              <Input
                inputMode={kind === 'number' || kind === 'money' ? 'decimal' : undefined}
                placeholder={
                  kind === 'text' && field === 'url' ? t('triggers.urlPlaceholder') : undefined
                }
                {...input}
              />
            </FormControl>
            {kind === 'money' ? (
              <span className="text-muted-foreground text-sm">{currency}</span>
            ) : null}
            {kind === 'number' && UNIT[field] ? (
              <span className="text-muted-foreground text-sm whitespace-nowrap">
                {t(`triggers.units.${UNIT[field]}`)}
              </span>
            ) : null}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  if (kind === 'attribute') {
    return (
      <div className="flex flex-col gap-2 sm:flex-row">
        <FormField
          control={form.control}
          name={`conditions.${index}.key`}
          render={({ field: input }) => (
            <FormItem className="sm:w-36">
              <FormLabel className="sr-only">
                {t('triggers.attributeKey', { position: index + 1 })}
              </FormLabel>
              <FormControl>
                <Input placeholder={t('triggers.attributeKeyPlaceholder')} {...input} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {valueInput}
      </div>
    );
  }
  return valueInput;
}
